import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import type { ContactStatus } from '@duke-hq/svc-api-db/client';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useEffect, useRef, useState } from 'react';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import {
  contactStatusMeta,
  contactStatusOrder,
  isContactStatus,
} from '../components/contactStatusMeta';
import { testIds } from './testIds';

interface ChangeContactStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  nominationId: string;
  currentContactStatus: ContactStatus;
  onSuccess: () => void;
}

export function ChangeContactStatusModal({
  isOpen,
  onClose,
  nominationId,
  currentContactStatus,
  onSuccess,
}: ChangeContactStatusModalProps): React.JSX.Element {
  const toast = useToast();
  const [selected, setSelected] = useState<ContactStatus>(currentContactStatus);
  const [initialStatus, setInitialStatus] = useState<ContactStatus>(currentContactStatus);

  // Keep the latest status in a ref so opening the modal seeds from the current
  // value without making a background refetch of currentContactStatus reset the
  // admin's in-progress selection.
  const currentContactStatusRef = useRef(currentContactStatus);
  currentContactStatusRef.current = currentContactStatus;

  useEffect(() => {
    if (isOpen) {
      setSelected(currentContactStatusRef.current);
      setInitialStatus(currentContactStatusRef.current);
    }
  }, [isOpen]);

  const mutation = trpcAdmin.workPlacements.placements.setNominationContactStatus.useMutation({
    onSuccess: () => {
      toast({ status: 'success', title: t`Contact status updated` });
      onSuccess();
      onClose();
    },
    onError: (error) => {
      toast({
        status: 'error',
        title: t`Failed to update contact status`,
        description: error.message,
      });
    },
  });

  const handleConfirm = (): void => {
    mutation.mutate({ nominationId, contactStatus: selected });
  };

  return (
    <FormModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      modalContentHeight="auto"
      modalContent={
        <FormMainContainer data-testid={testIds.changeContactStatusModal}>
          <FormContentContainer>
            <Text fontSize="lg" fontWeight="semibold" w="full">
              <Trans>Change contact status</Trans>
            </Text>

            <FormControl isRequired>
              <FormLabel>
                <Trans>Status</Trans>
              </FormLabel>
              <RadioGroup
                value={selected}
                onChange={(val) => {
                  if (isContactStatus(val)) {
                    setSelected(val);
                  }
                }}
              >
                <Stack gap={2}>
                  {contactStatusOrder.map((status) => {
                    const isSelected = selected === status;
                    return (
                      <Box
                        key={status}
                        as="label"
                        display="flex"
                        alignItems="center"
                        px={4}
                        py={3}
                        borderWidth="1px"
                        borderRadius="md"
                        borderColor={isSelected ? 'purple.500' : 'border.default'}
                        bg="bg.canvas"
                        cursor="pointer"
                      >
                        <Radio value={status} colorScheme="purple" pointerEvents="none">
                          {contactStatusMeta[status].label()}
                        </Radio>
                      </Box>
                    );
                  })}
                </Stack>
              </RadioGroup>
            </FormControl>
          </FormContentContainer>

          <FormFooterContainer>
            <Button
              isLoading={mutation.isPending}
              isDisabled={selected === initialStatus}
              onClick={handleConfirm}
            >
              <Trans>Submit</Trans>
            </Button>
            <Button variant="ghost" onClick={onClose}>
              <Trans>Cancel</Trans>
            </Button>
          </FormFooterContainer>
        </FormMainContainer>
      }
    />
  );
}
